// src/components/ForgotPassword.jsx
//
// Three-step, email-based password reset, now matching three separate
// C:\CET-HTML-Code mockup screens field-for-field:
//   1. Enter email    -- reset-password.html
//   2. Enter 6-digit OTP (six separate boxes, auto-advancing focus)
//                      -- verify-otp-update-password.html
//   3. Enter new password (input-group + eye-toggle + live requirements
//      checklist)      -- update-password.html
//
// All three share the same auth-box/row/col-md-7/col-md-5 shell Login.jsx
// and the rebuilt AccessRequestForm.jsx already use.
//
// IMPORTANT backend note: there is no separate "verify OTP" endpoint --
// app.py's /reset-password checks the OTP and sets the new password in
// one call (see the original two-step version of this file). So step 2's
// "Verify OTP" button only checks client-side that all 6 boxes are
// filled and advances to step 3; the OTP itself is still verified
// together with the new password when step 3 actually submits. If the
// OTP was wrong, the error surfaces on step 3 (via `msg`) rather than
// bouncing the user back to step 2 -- splitting verification into its
// own real round trip would need a new backend endpoint, which wasn't
// part of this request.
import React, { useRef, useState } from "react";
import axios from "axios";
import { API_ORIGIN } from "../config/apiConfig";
import PasswordChecklist, { isPasswordValid } from "./PasswordField";

// FIX: was hardcoded to "/api" -- see the same fix in
// Step1ProjectInfo.jsx; every other page derives this from API_ORIGIN.
// Both /forgot-password and /reset-password are public (no JWT needed),
// so this file still uses plain axios rather than the wrapped `api`
// instance -- only the origin needed fixing here.
const API_BASE = API_ORIGIN;

export default function ForgotPassword() {
  const [step, setStep] = useState(1); // 1 = email, 2 = OTP, 3 = new password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(""); // up to 6 digits, one per box
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [msg, setMsg] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const otpBoxRefs = useRef([]);

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    setMsg("");
    setSuccess("");
    if (!email) {
      setMsg("Enter your email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/forgot-password`, { email });
      if (res.data && res.data.otp_required) {
        setStep(2);
        setSuccess("An OTP has been sent to your email.");
      } else {
        setMsg(res.data?.message || "Unable to send OTP");
      }
    } catch (err) {
      setMsg(err.response?.data?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpBoxChange = (index, rawValue) => {
    // FIX: was `rawValue.replace(/[^0-9]/g, "").slice(-1)` -- only ever kept
    // the LAST digit of whatever landed here, so a multi-digit rawValue
    // (e.g. mobile "fill from SMS" autofill delivered as a plain onChange
    // rather than a clipboard paste event) silently collapsed to one
    // digit in one box instead of filling forward. Same fill-forward
    // behavior as the dedicated paste handler below, just triggered a
    // different way. Normal single-digit typing (each box has
    // maxLength=1) still lands in the else branch below, unchanged.
    const digits = rawValue.replace(/[^0-9]/g, "");
    if (digits.length > 1) {
      const boxes = otp.padEnd(6, " ").split("");
      let i = index;
      for (const d of digits) {
        if (i > 5) break;
        boxes[i] = d;
        i++;
      }
      setOtp(boxes.join("").trimEnd());
      otpBoxRefs.current[Math.min(i, 5)]?.focus();
      return;
    }
    const boxes = otp.padEnd(6, " ").split("");
    boxes[index] = digits || " ";
    setOtp(boxes.join("").trimEnd());
    if (digits && index < 5) {
      otpBoxRefs.current[index + 1]?.focus();
    }
  };

  // FIX: pasting the full 6-digit OTP into any one box used to only ever
  // fill that single box, because each box also has maxLength={1} -- the
  // browser truncates a paste down to one character before onChange even
  // fires, so fixing handleOtpBoxChange alone isn't enough. Reading the
  // clipboard directly here (before the browser applies its own truncated
  // paste, which preventDefault stops from happening at all) lets a
  // single paste distribute across every box starting from wherever it
  // was pasted. Same fix already shipped on OtpVerify.jsx's identical
  // 6-box OTP UI.
  const handleOtpBoxPaste = (index, e) => {
    const pasted = (e.clipboardData || window.clipboardData)?.getData("text") || "";
    const digits = pasted.replace(/[^0-9]/g, "");
    if (!digits) return;
    e.preventDefault();
    const boxes = otp.padEnd(6, " ").split("");
    let i = index;
    for (const d of digits) {
      if (i > 5) break;
      boxes[i] = d;
      i++;
    }
    setOtp(boxes.join("").trimEnd());
    otpBoxRefs.current[Math.min(i, 5)]?.focus();
  };

  const handleOtpBoxKeyDown = (index, e) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      otpBoxRefs.current[index - 1]?.focus();
    }
  };

  const verifyOtp = (e) => {
    if (e) e.preventDefault();
    setMsg("");
    if (otp.trim().length < 6) {
      setMsg("Enter the 6-digit OTP sent to your email");
      return;
    }
    setStep(3);
  };

  const resetPassword = async (e) => {
    if (e) e.preventDefault();
    setMsg("");
    setSuccess("");
    if (!isPasswordValid(newPassword)) {
      setMsg("Password does not meet all the requirements below");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/reset-password`, {
        email,
        otp: otp.trim(),
        newPassword,
        confirmPassword: newPassword,
      });
      if (res.data && res.data.success) {
        setSuccess("Your password has been reset. Redirecting to login...");
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      } else {
        setMsg(res.data?.message || "Unable to reset password");
      }
    } catch (err) {
      setMsg(err.response?.data?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const startOver = (e) => {
    if (e) e.preventDefault();
    setStep(1);
    setOtp("");
    setNewPassword("");
    setMsg("");
    setSuccess("");
  };

  return (
    <div className="auth-box p-0 w-100">
      <div className="row w-100 g-0">
        <div className="col-md-7 d-none d-md-block">
          <div
            className="h-100 position-relative card-side-img rounded-0 overflow-hidden"
            style={{ backgroundImage: "url(/assets/images/logo/CET%20Background-withoutlogo.png)", backgroundSize: "cover", backgroundPosition: "center" }}
          >
            <div className="p-4 card-img-overlay auth-overlay d-flex align-items-end justify-content-center" />
          </div>
        </div>

        <div className="col-md-5">
          <div className="card auth-box-form border-0 mb-0">
            <div className="card-body min-vh-100 position-relative d-flex flex-column justify-content-center">
              <div className="auth-brand mb-3 text-center">
                <img
                  src="/assets/images/logo/CET-Full-logo.png"
                  alt="Logo"
                  style={{ maxWidth: 220 }}
                />
              </div>

              {msg && (
                <div className="alert alert-danger py-2 mb-3" role="alert" aria-live="polite">
                  {msg}
                </div>
              )}
              {success && (
                <div className="alert alert-success py-2 mb-3" role="alert" aria-live="polite">
                  {success}
                </div>
              )}

              {step === 1 && (
                <>
                  <p className="text-muted text-center auth-sub-text mx-auto">
                    Enter your email address and we&rsquo;ll send you an OTP to reset your password.
                  </p>

                  <form className="mt-4" onSubmit={requestOtp}>
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        Email address <span className="text-danger">*</span>
                      </label>
                      <div className="app-search">
                        <input
                          id="email"
                          type="email"
                          className="form-control"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="username"
                          required
                        />
                        <i className="ti ti-mail app-search-icon text-muted" />
                      </div>
                    </div>

                    <div className="d-grid">
                      <button type="submit" className="btn btn-primary fw-semibold py-2" disabled={loading}>
                        {loading ? "Sending..." : "Request OTP"}
                      </button>
                    </div>
                  </form>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="text-center">
                    <h4 className="fw-bold text-dark">OTP sent on {email}</h4>
                  </div>

                  <form className="mt-4" onSubmit={verifyOtp}>
                    <div className="mb-3">
                      <label className="form-label d-flex">
                        Enter your 6-digit code <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex gap-2 two-factor">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <input
                            key={i}
                            ref={(el) => (otpBoxRefs.current[i] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            className="form-control text-center"
                            value={otp[i] && otp[i] !== " " ? otp[i] : ""}
                            onChange={(e) => handleOtpBoxChange(i, e.target.value)}
                            onKeyDown={(e) => handleOtpBoxKeyDown(i, e)}
                            onPaste={(e) => handleOtpBoxPaste(i, e)}
                            required
                          />
                        ))}
                      </div>
                    </div>

                    <div className="d-grid">
                      <button type="submit" className="btn btn-primary fw-semibold py-2">
                        Verify OTP
                      </button>
                    </div>
                  </form>

                  <p className="text-muted text-center mt-4 mb-0">
                    Don&rsquo;t have a code?{" "}
                    <a href="#" className="link-offset-2 fw-bold" onClick={requestOtp}>
                      Resend
                    </a>
                  </p>
                </>
              )}

              {step === 3 && (
                <>
                  <div className="text-center">
                    <h3 className="fw-bold text-dark">Update Password</h3>
                  </div>

                  <form className="mt-3" onSubmit={resetPassword}>
                    <div className="mb-3">
                      <label htmlFor="newPassword" className="form-label">
                        New Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          id="newPassword"
                          className="form-control"
                          type={showPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          autoComplete="new-password"
                          required
                        />
                        <button
                          className="btn border"
                          type="button"
                          onClick={() => setShowPassword((s) => !s)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
                        </button>
                      </div>

                      <PasswordChecklist password={newPassword} />
                    </div>

                    <div className="d-grid">
                      <button type="submit" className="btn btn-primary fw-semibold py-2" disabled={loading}>
                        {loading ? "Updating..." : "Update Password"}
                      </button>
                    </div>
                  </form>
                </>
              )}

              {step > 1 && (
                <p className="text-muted text-center mt-4 mb-0">
                  <a href="#" className="link-offset-3 fw-bold" onClick={startOver}>
                    Use a different email
                  </a>
                </p>
              )}

              <p className="text-muted text-center mt-4 mb-0">
                Return to{" "}
                <a href="/" className="link-offset-3 fw-bold">Sign in</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
